/**
 * Campaign Quick Navigation Component
 *
 * **Purpose: System Navigation (Primary) + Workflow Guidance (Secondary)**
 *
 * This component serves TWO distinct functions:
 * 1. **System Navigation (Primary)**
 *    - Provides direct links to all pages in the campaign
 *    - Visual indicator for current location (violet highlight + checkmark)
 *    - Preserves query params during navigation
 *    - Sticky positioning for constant access
 *
 * 2. **Workflow Guidance (Secondary)**
 *    - Items are ordered based on functional_type workflow
 *    - Reflects campaign's natural progression
 *    - Does NOT enforce or block navigation — user can visit any page at any time
 *    - Visual identity indicator shows if campaign has PhD visual profile
 *
 * RULE: This is NOT a stepper/progress tracker. Users can jump to any page.
 *       The order provides context, but navigation is free-form.
 */

import {
    Bug,
    Building2,
    CheckCircle2,
    Eye,
    ChevronDown,
    FileText,
    Layout,
    Map,
    Network,
    Package,
    Palette,
    Target,
    Users,
    Zap,
    Database,
    ListChecks,
    Magnet,
    GitCommit,
    Radio,
    Send,
    Globe,
    MessageSquare,
    BarChart3,
    Mic,
    MousePointer2,
    Share2,
    BookOpen,
} from 'lucide-react'
import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { useCampaignData } from '../hooks/useCampaignData'
import { getWorkflowSteps } from '../utils/workflow'
import { visualEngineService } from '@/services/visual-engine'

/** Attempt to extract a primary brand hex color from a visual identity run response */
function extractBrandPrimary(run: any): string | null {
    if (!run) return null
    // Try common paths where primary color might live
    const candidates = [
        run?.colorTheory?.palette?.[0],
        run?.phase3?.colorTheory?.palette?.[0],
        run?.controls?.qualityControls?.colors?.[0],
        run?.qualityControls?.colors?.[0],
        run?.brandColors?.primary,
        run?.foundation?.brandColors?.primary,
    ]
    const hex = candidates.find(c => typeof c === 'string' && c.startsWith('#'))
    return hex ?? null
}

interface NavItem {
    id: string
    label: string
    path: string
    icon: any
    description: string
    children?: NavItem[]
}

interface CampaignQuickNavProps {
    currentPage?: string // e.g., 'targets', 'tfd', 'alos', 'story', 'framework', 'strategic-overview'
    className?: string
    campaignId?: string // Optional override if route param name is different
}

export function CampaignQuickNav({ currentPage, className = '', campaignId: propCampaignId }: CampaignQuickNavProps) {
    const navigate = useNavigate()
    const location = useLocation()
    const params = useParams()
    const campaignId = propCampaignId || params.campaignId || params.id // Support both :id and :campaignId params
    const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)
    const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null)
    const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const triggerRefs = useRef<Record<string, HTMLDivElement | null>>({})

    // 🆕 Helper to preserve query params during navigation
    const navigateWithParams = (path: string) => {
        const search = location.search
        navigate(`${path}${search}`)
    }

    const handleMouseEnter = useCallback((itemId: string) => {
        if (dropdownTimeoutRef.current) {
            clearTimeout(dropdownTimeoutRef.current)
            dropdownTimeoutRef.current = null
        }
        const el = triggerRefs.current[itemId]
        if (el) {
            const rect = el.getBoundingClientRect()
            setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX })
        }
        setOpenDropdownId(itemId)
    }, [])

    const handleMouseLeave = () => {
        dropdownTimeoutRef.current = setTimeout(() => {
            setOpenDropdownId(null)
        }, 300) // 300ms buffer to reach the menu
    }

    // Clean up timeout on unmount
    useEffect(() => {
        return () => {
            if (dropdownTimeoutRef.current) {
                clearTimeout(dropdownTimeoutRef.current)
            }
        }
    }, [])

    // Fetch campaign data to get the functional_type
    const { campaign } = useCampaignData(campaignId || undefined)
    // If the campaign has a source_storyboard_id, it's using the 5-step story hub flow
    const effectiveFunctionalType: any = (campaign as any)?.source_storyboard_id
        ? 'influencer_v2'
        : ((campaign?.functional_type as any) || 'narrative')
    const functionalType = effectiveFunctionalType

    // Guard: Don't render if no campaignId (e.g., on campaigns list page)
    const hasVisualPhD = !!(campaign as any)?.visual_run_id
    const [brandPrimary, setBrandPrimary] = useState<string | null>(null)

    const visualRunId = (campaign as any)?.visual_run_id

    // Fetch brand color from visual identity run for active step indicator
    useEffect(() => {
        if (!visualRunId) return
        visualEngineService.getRun(visualRunId).then(run => {
            const color = extractBrandPrimary(run)
            if (color) setBrandPrimary(color)
        }).catch(() => { /* brand color is optional */ })
    }, [visualRunId])

    if (!campaignId) {
        return null
    }

    // Child IDs that appear under parent dropdowns (excluded from top-level nav)
    // your-story and audit are nested under their parent dropdown items
    const CHILD_IDS = ['your-story', 'audit']

    // Mapping of step IDs to icons and descriptions
    const stepMetadata: Record<string, { icon: any; description: string; children?: NavItem[] }> = {
        // ── influencer_v2 (storyboard-first execution flow) steps ──
        'import-storyboard': { icon: BookOpen, description: 'Import the storyboard your creator brief is built from' },
        'creator-setup': { icon: Users, description: 'Adapt your brief for the creator archetype and platform' },
        moments: { icon: Zap, description: 'Generate and review moment briefs for creator execution' },
        overview: { icon: Building2, description: 'Campaign dashboard' },
        // ── Foundation (replaces strategy/alos/targets/tfd) ──
        foundation: { icon: Target, description: 'Your brand baseline — audience mindset and proof points' },
        story: {
            icon: FileText,
            description: 'Campaign story planning and narrative',
            children: [
                {
                    id: 'your-story',
                    label: 'Campaign Story',
                    path: `/dashboard/campaigns/${campaignId}/your-story`,
                    icon: FileText,
                    description: 'Your campaign story',
                },
                {
                    id: 'framework',
                    label: 'Framework',
                    path: `/dashboard/campaigns/${campaignId}/story-architecture`,
                    icon: Network,
                    description: 'Story framework and structure',
                },
                {
                    id: 'strategic-overview',
                    label: 'Strategy',
                    path: `/dashboard/campaigns/${campaignId}/strategic-overview`,
                    icon: Map,
                    description: 'Campaign strategy and story section structure',
                },
            ],
        },
        topics: {
            icon: FileText,
            description: 'Topic ideation and management',
            children: [
                { id: 'visual-identity', label: 'Visual Identity', path: `/dashboard/campaigns/${campaignId}/visual-identity`, icon: Layout, description: 'Visual concepts and identity' },
            ],
        },
        'visual-identity': { icon: Layout, description: 'Visual concepts and identity' },
        'topic-builder': { icon: Layout, description: 'Visual Concepts' },
        'story-map': {
            icon: Map,
            description: 'Match proof points to each story section',
            children: [
                { id: 'story-readback', label: 'Story Read-Back', path: `/dashboard/campaigns/${campaignId}/story-readback`, icon: BookOpen, description: 'Section-by-section narrative read-back' },
            ],
        },
        'story-readback': { icon: BookOpen, description: 'Section-by-section narrative read-back' },
        dataset: { icon: Database, description: 'Bulk dataset management' },
        mapping: { icon: ListChecks, description: 'Dataset field mapping' },
        'mega-asset': { icon: FileText, description: 'The primary content this campaign is built from' },
        'fan-out': { icon: Share2, description: 'Generate many channel-ready variants from one source' },
        scheduling: { icon: GitCommit, description: 'Multi-week send schedule' },
        assets: { icon: Package, description: 'Tactical asset generation' },
        bait: { icon: Magnet, description: 'Hooks and entry point builder' },
        corridor: { icon: GitCommit, description: 'Message sequence and conversion path' },
        newsroom: { icon: Radio, description: 'Press kit and newsroom assets' },
        outreach: { icon: Send, description: 'Media outreach and pitch builder' },
        'story-library': { icon: BookOpen, description: 'Select approved proof story assets' },
        intent: { icon: Globe, description: 'Connect proof points to search intent' },
        pillars: { icon: Layout, description: 'Pillar pages and cluster structure' },
        voice: { icon: Mic, description: 'Brand voice and persona tuning' },
        hooks: { icon: MousePointer2, description: 'Social hooks built for fast scroll environments' },
        matrix: { icon: BarChart3, description: 'Creative matrix and targeting grid' },
        variations: { icon: MessageSquare, description: 'A/B variations and ad copy' },
        'command-center': { icon: Zap, description: 'Review and export your campaign' },
        'landing-page-goals': { icon: Target, description: 'Define what this page needs to do and what a conversion looks like' },
        'landing-page-visual': { icon: Layout, description: 'Visual identity and page mockup' },
        'landing-page-sections': { icon: Layout, description: 'Choose page sections and outcome alignment' },
        'landing-page-copy-lab': { icon: MessageSquare, description: 'Select and refine copy variants' },
        'landing-page-preview': { icon: Eye, description: 'Preview page structure before publishing' },
        debug: {
            icon: Bug,
            description: 'Diagnostics (admin only)',
            children: [
                { id: 'audit', label: 'Audit', path: `/dashboard/campaigns/${campaignId}/audit`, icon: ListChecks, description: 'Campaign diagnostics and activity log' },
            ],
        },
        audit: { icon: ListChecks, description: 'Campaign diagnostics and activity log' },
        // ── HEFT Persona steps ──
        'story-selection': { icon: BookOpen, description: 'Choose proof stories from the subject\'s story bank' },
        'identity-snapshot': { icon: Users, description: 'Curate star points and evidence inventory' },
        deliverables: { icon: Package, description: 'Generate bios, hooks, and pitches' },
        'case-study': { icon: FileText, description: 'Generate long-form case study and social proof snippets' },
    }

    const workflowSteps = getWorkflowSteps(functionalType)

    const LEGACY_STEP_IDS = ['script-builder', 'storyboard']

    // Convert workflow steps to NavItems (exclude child steps that appear in parent dropdowns)
    const allNavItems: NavItem[] = [
        ...workflowSteps
            .filter(step => !CHILD_IDS.includes(step.id))
            .map(step => ({
                id: step.id,
                label: step.label,
                path: `/dashboard/campaigns/${campaignId}${step.path}`,
                icon: stepMetadata[step.id]?.icon || Building2,
                description: stepMetadata[step.id]?.description || '',
                children: stepMetadata[step.id]?.children,
            })),
    ]

    const navItems = allNavItems.filter(item => !LEGACY_STEP_IDS.includes(item.id))
    const legacyNavItems = allNavItems.filter(item => LEGACY_STEP_IDS.includes(item.id))

    // Active state for parent items (when any child or self is current)
    const isStoryActive = ['story', 'your-story', 'framework', 'strategic-overview'].includes(currentPage || '')
    const isTopicsActive = ['topics', 'visual-identity'].includes(currentPage || '')
    const isStoryMapActive = ['story-map', 'story-readback'].includes(currentPage || '')
    const isDebugActive = false

    // Build dynamic active styles using brand color when available
    const activeBg = brandPrimary ? `${brandPrimary}18` : undefined  // 10% opacity
    const activeText = brandPrimary ?? undefined
    const activeBadge = brandPrimary ?? undefined

    return (
        <>
        <div className={`bg-white border-b border-gray-200 sticky top-[72px] z-[45] shadow-sm ${className}`}>
            <div className='px-6'>
                {hasVisualPhD && (
                    <div className='flex items-center justify-end gap-2 py-1.5 text-xs text-slate-600 border-b border-slate-100'>
                        <Palette className='w-3.5 h-3.5 text-violet-500' />
                        <span>Brand Visual Profile (PhD) linked</span>
                        <CheckCircle2 className='w-3.5 h-3.5 text-emerald-500' />
                    </div>
                )}
                <div className='overflow-x-auto'>
                <nav key={effectiveFunctionalType} className='flex items-center gap-1 py-2 min-w-max'>
                    {navItems.map(item => {
                        const Icon = item.icon
                        const isCurrent = item.id === currentPage
                        const hasChildren = item.children && item.children.length > 0
                        const isActive = hasChildren
                            ? (item.id === 'story' && isStoryActive) ||
                              (item.id === 'topics' && isTopicsActive) ||
                              (item.id === 'story-map' && isStoryMapActive) ||
                              (item.id === 'debug' && isDebugActive)
                            : isCurrent

                        if (hasChildren) {
                            return (
                                <div
                                    key={item.id}
                                    ref={el => { triggerRefs.current[item.id] = el }}
                                    className='relative'
                                    onMouseEnter={() => handleMouseEnter(item.id)}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    <button
                                        onClick={() => navigateWithParams(item.path)}
                                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                                            isActive
                                                ? activeBg ? '' : 'bg-violet-100 text-violet-900 shadow-sm'
                                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                        }`}
                                        style={isActive && activeBg ? { backgroundColor: activeBg, color: activeText, boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)' } : undefined}
                                        title={item.description}
                                    >
                                        <Icon className={`w-4 h-4 ${isActive && !activeBadge ? 'text-violet-600' : ''}`} style={isActive && activeBadge ? { color: activeBadge } : undefined} />
                                        <span>
                                            {item.id === 'story' ? 'Story & Strategy' : item.id === 'strategy' ? 'Goals' : item.id === 'story-map' ? 'Story Map' : item.label}
                                        </span>
                                        <ChevronDown className={`w-3 h-3 ${isActive && !activeBadge ? 'text-violet-600' : ''}`} style={isActive && activeBadge ? { color: activeBadge } : undefined} />
                                        {isActive && <CheckCircle2 className={`w-3.5 h-3.5 ${!activeBadge ? 'text-violet-600' : ''}`} style={activeBadge ? { color: activeBadge } : undefined} />}
                                    </button>
                                </div>
                            )
                        }

                        return (
                            <button
                                key={item.id}
                                onClick={() => navigateWithParams(item.path)}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                                    isCurrent
                                        ? activeBg ? '' : 'bg-violet-100 text-violet-900 shadow-sm'
                                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                }`}
                                style={isCurrent && activeBg ? { backgroundColor: activeBg, color: activeText, boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)' } : undefined}
                                title={item.description}
                            >
                                <Icon className={`w-4 h-4 ${isCurrent && !activeBadge ? 'text-violet-600' : ''}`} style={isCurrent && activeBadge ? { color: activeBadge } : undefined} />
                                <span>{item.label}</span>
                                {isCurrent && <CheckCircle2 className={`w-3.5 h-3.5 ${!activeBadge ? 'text-violet-600' : ''}`} style={activeBadge ? { color: activeBadge } : undefined} />}
                            </button>
                        )
                    })}

                    {/* Legacy tools — separated from current campaign tools */}
                    {legacyNavItems.length > 0 && (
                        <>
                            <div className='mx-2 h-6 w-px bg-gray-200 shrink-0' aria-hidden='true' />
                            <span className='text-[10px] text-gray-400 uppercase tracking-wide whitespace-nowrap px-1'>
                                Legacy
                            </span>
                            {legacyNavItems.map(item => {
                                const Icon = item.icon
                                const isCurrent = item.id === currentPage
                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => navigateWithParams(item.path)}
                                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap opacity-60 hover:opacity-80 ${
                                            isCurrent
                                                ? 'bg-gray-100 text-gray-700'
                                                : 'text-gray-500 hover:bg-gray-50'
                                        }`}
                                        title={item.description}
                                    >
                                        <Icon className='w-4 h-4' />
                                        <span>{item.label}</span>
                                        {isCurrent && <CheckCircle2 className='w-3.5 h-3.5 text-gray-500' />}
                                    </button>
                                )
                            })}
                        </>
                    )}
                </nav>
                </div>
            </div>
        </div>

        {/* Portal dropdown — renders outside overflow containers */}
        {openDropdownId && dropdownPos && (() => {
            const openItem = navItems.find(i => i.id === openDropdownId)
            if (!openItem?.children) return null
            return createPortal(
                <div
                    style={{ position: 'absolute', top: dropdownPos.top + 4, left: dropdownPos.left, zIndex: 9999 }}
                    onMouseEnter={() => {
                        if (dropdownTimeoutRef.current) {
                            clearTimeout(dropdownTimeoutRef.current)
                            dropdownTimeoutRef.current = null
                        }
                    }}
                    onMouseLeave={handleMouseLeave}
                >
                    <div className='w-56 bg-white rounded-lg shadow-2xl border border-gray-200 py-2'>
                        {openItem.children.map(child => {
                            const ChildIcon = child.icon
                            const isChildCurrent = child.id === currentPage
                            return (
                                <button
                                    key={child.id}
                                    onClick={() => {
                                        navigateWithParams(child.path)
                                        setOpenDropdownId(null)
                                    }}
                                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                                        isChildCurrent
                                            ? activeBg ? '' : 'bg-violet-50 text-violet-900 font-medium'
                                            : 'text-gray-700 hover:bg-gray-50'
                                    }`}
                                    style={isChildCurrent && activeBg ? { backgroundColor: activeBg, color: activeText, fontWeight: 500 } : undefined}
                                >
                                    <ChildIcon className={`w-4 h-4 ${isChildCurrent && !activeBadge ? 'text-violet-600' : isChildCurrent ? '' : 'text-gray-400'}`} style={isChildCurrent && activeBadge ? { color: activeBadge } : undefined} />
                                    <span className='flex-1 text-left'>{child.label}</span>
                                    {isChildCurrent && <CheckCircle2 className={`w-3.5 h-3.5 ${!activeBadge ? 'text-violet-600' : ''}`} style={activeBadge ? { color: activeBadge } : undefined} />}
                                </button>
                            )
                        })}
                    </div>
                </div>,
                document.body
            )
        })()}
        </>
    )
}
